import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { getSwaggerServiceBaseUrl } from "../../config/apiBase";
import { readAuthUser } from "../../utils/authUser";
import ModulePage, { ModuleToolbar } from "../../shared-ui/ModulePage";
import { EmptyState, ErrorState, Skeleton } from "../../shared-ui/States";
import "../../shared-ui/crane-tokens.css";

const token = () => localStorage.getItem("zTaskAuthToken") || "";

function normalizeTasks(payload) {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.tasks)) return payload.tasks;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.message)) return payload.message;
  return [];
}

function statusOf(task) {
  return String(task?.status || task?.taskStatus || task?.current_status || "—").trim();
}

function titleOf(task) {
  return task?.title || task?.taskName || task?.name || `Task #${task?.taskId || task?.id || "?"}`;
}

function assigneeOf(task) {
  return (
    task?.assigneeName ||
    task?.assignedToName ||
    task?.assigned_to_name ||
    task?.assignee ||
    task?.assignedTo ||
    task?.userName ||
    ""
  );
}

function projectOf(task) {
  return task?.projectName || task?.project_name || task?.project || "";
}

function taskIdOf(task) {
  return task?.taskId ?? task?.id;
}

function formatDue(task) {
  const due = task?.dueDate || task?.due_date;
  if (!due) return "—";
  const d = new Date(due);
  if (Number.isNaN(d.getTime())) return String(due).slice(0, 10);
  return d.toISOString().slice(0, 10);
}

async function fetchTasks(headers) {
  const base = getSwaggerServiceBaseUrl();
  const allRes = await fetch(`${base}/api/get-task`, { headers });
  const allBody = await allRes.json().catch(() => ({}));
  // Legacy API returned 404 when empty — treat as empty list, not an error.
  if (allRes.status === 404) return [];
  if (!allRes.ok) throw new Error(allBody.message || `HTTP ${allRes.status}`);
  let tasks = normalizeTasks(allBody);
  if (tasks.length === 0) {
    const { user } = readAuthUser();
    const userId = user?.id ?? user?.userId;
    if (userId) {
      const scopedRes = await fetch(`${base}/api/get-task-by-userId?userId=${userId}`, { headers });
      if (scopedRes.status === 404) return [];
      if (scopedRes.ok) {
        const scopedBody = await scopedRes.json().catch(() => ({}));
        tasks = normalizeTasks(scopedBody);
      }
    }
  }
  return tasks;
}

export default function StitchTasksPage() {
  const [tasks, setTasks] = useState([]);
  const [busy, setBusy] = useState(true);
  const [err, setErr] = useState("");
  const [query, setQuery] = useState("");

  const headers = useMemo(
    () => ({
      Authorization: `Bearer ${token()}`,
      Accept: "application/json",
      "X-Client": "web",
    }),
    []
  );

  const load = useCallback(async () => {
    setBusy(true);
    setErr("");
    try {
      setTasks(await fetchTasks(headers));
    } catch (e) {
      setErr(e.message || "Failed to load tasks");
      setTasks([]);
    } finally {
      setBusy(false);
    }
  }, [headers]);

  useEffect(() => {
    load();
  }, [load]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return tasks;
    return tasks.filter((task) => {
      const hay = [titleOf(task), statusOf(task), assigneeOf(task), projectOf(task)]
        .join(" ")
        .toLowerCase();
      return hay.includes(q);
    });
  }, [tasks, query]);

  return (
    <ModulePage
      crumbs={["Operations", "ZTask", "Tasks"]}
      title="Tasks"
      description="Workboard backed by your tenant task API."
      actions={
        <>
          <button type="button" className="crane-btn" onClick={load} disabled={busy}>
            Refresh
          </button>
          <Link to="/tasks/create" className="crane-btn crane-btn--primary" style={{ textDecoration: "none" }}>
            + Create task
          </Link>
        </>
      }
    >
      <ModuleToolbar>
        <input
          type="search"
          className="crane-input"
          placeholder="Search title, status, assignee…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          aria-label="Filter tasks"
          style={{ maxWidth: 360 }}
        />
      </ModuleToolbar>

      {busy && <Skeleton rows={6} height={40} />}
      {!busy && err && <ErrorState title="Could not load tasks" description={err} onRetry={load} />}
      {!busy && !err && filtered.length === 0 && (
        <EmptyState
          title={query ? "No matching tasks" : "No tasks yet"}
          description={
            query
              ? "Try a different search term."
              : "Create a task or wait for assignments from your team."
          }
          action={
            !query ? (
              <Link to="/tasks/create" className="crane-btn crane-btn--primary" style={{ textDecoration: "none" }}>
                Create task
              </Link>
            ) : null
          }
        />
      )}
      {!busy && !err && filtered.length > 0 && (
        <div className="crane-card" style={{ overflowX: "auto" }}>
          <table className="crane-table">
            <thead>
              <tr>
                <th>Title</th>
                <th>Status</th>
                <th>Priority</th>
                <th>Due</th>
                <th>Project</th>
                <th>Assignee</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((task) => {
                const id = taskIdOf(task);
                return (
                  <tr key={id ?? titleOf(task)}>
                    <td>
                      {id != null ? (
                        <Link to={`/tasks/${id}`}>{titleOf(task)}</Link>
                      ) : (
                        titleOf(task)
                      )}
                    </td>
                    <td>{statusOf(task)}</td>
                    <td>{task?.priority || "—"}</td>
                    <td>{formatDue(task)}</td>
                    <td>{projectOf(task) || "—"}</td>
                    <td>{assigneeOf(task) || "—"}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </ModulePage>
  );
}
